import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { ThemeProvider } from '@/components/theme-provider'
import { ModeToggle } from '@/components/mode-toggle'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { CheckCircle, LogOut, ArrowRight, ExternalLink } from 'lucide-react'
import { clearToken } from '@/lib/sso'

export default function LogoutPage() {
  const { t } = useTranslation('common')
  const location = useLocation()

  useEffect(() => {
    // 页面加载时清除token
    clearToken()
  }, [])

  // 从当前路径获取语言前缀
  const getLanguagePrefix = () => {
    const path = location.pathname
    if (path.startsWith('/zh/')) return '/zh'
    if (path.startsWith('/en/')) return '/en'
    return '/zh' // 默认中文
  }

  const handleGoToLogin = () => {
    // 跳转到对应语言的根路径
    const langPrefix = getLanguagePrefix()
    window.location.href = langPrefix
  }

  const handleVisitWebsite = () => {
    // 跳转到MemEcho官方网站
    const websiteUrl = import.meta.env.VITE_MEMECHO_DOMAIN || 'https://memecho.ai'
    window.open(websiteUrl, '_blank')
  }

  return (
    <ThemeProvider
      defaultTheme="system"
      storageKey="theme"
    >
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="absolute top-4 right-4 flex items-center space-x-2">
          <LanguageSwitcher />
        </div>
        
        <div className="w-full max-w-md space-y-6">
          {/* 页面标题 */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/favicon.png" 
                alt="MemEcho" 
                className="w-8 h-8"
              />
              <h1 className="text-2xl font-bold tracking-tight">{t('logout.pageTitle')}</h1>
            </div>
          </div>



          <Card>
            <CardHeader className="pb-4">
              {/* <CardTitle className="text-lg">{t('logout.confirmation.title')}</CardTitle>
              <CardDescription className="text-sm">
                {t('logout.confirmation.description')}
              </CardDescription> */}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 登出成功提示 */}
              <div className="text-center py-6">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-xl font-semibold mb-2">{t('logout.title')}</h2>
                <p className="text-muted-foreground text-sm">
                  {t('logout.subtitle')}
                </p>
              </div>

              {/* 简化的状态信息 */}
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• {t('logout.details.token_cleared')}</p>
                <p>• {t('logout.details.session_ended')}</p>
              </div>

              {/* 操作按钮 */}
              <div className="flex flex-col space-y-3">
                <Button 
                  onClick={handleGoToLogin} 
                  className="w-full"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {t('logout.actions.go_to_login')}
                </Button>
                
                <Button 
                  onClick={handleVisitWebsite} 
                  variant="outline" 
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('logout.actions.visit_website')}
                </Button>
              </div>

              <div className="text-xs text-muted-foreground text-center pt-2">
                {t('logout.footer')}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ThemeProvider>
  )
}
